import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { getDisplayTitle } from "../../shared/constants/tagConstants";
import brStyles from "./TagsBrandRefresh.module.css";

const Tags = ({
  tags = [],
  defaultActive,
  onChange,
  className = "",
  isBrandRefresh = false,
}) => {
  const [activeTag, setActiveTag] = useState(defaultActive || null);
  const presetBoxRef = useRef();

  useEffect(() => {
    const el = presetBoxRef.current;
    if (!el) return undefined;

    let onScroll;
    if (isBrandRefresh) {
      const hasHorizontalRoom = () => el.scrollWidth > el.clientWidth + 1;

      onScroll = (e) => {
        if (e.deltaY === 0) return;

        const atStart = el.scrollLeft <= 0;
        const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
        const scrollingRight = e.deltaY > 0;
        const scrollingLeft = e.deltaY < 0;

        // If the carousel cannot scroll further in the requested direction, let
        // the browser handle the event so the page can scroll instead of being eaten.
        if ((scrollingRight && atEnd) || (scrollingLeft && atStart)) return;

        e.preventDefault();
        el.scrollTo({
          left: el.scrollLeft + e.deltaY,
          behavior: "smooth",
        });
      };

      // Only intercept wheel when the carousel has horizontal room to scroll.
      // Otherwise we would preventDefault and the page could not scroll.
      if (hasHorizontalRoom()) {
        el.addEventListener("wheel", onScroll, { passive: false });
      }
    } else {
      onScroll = (e) => {
        if (e.deltaY === 0) return;
        e.preventDefault();
        el.scrollTo({
          left: el.scrollLeft + e.deltaY,
          behavior: "smooth",
        });
      };
      el.addEventListener("wheel", onScroll, { passive: false });
    }

    return () => {
      el.removeEventListener("wheel", onScroll);
    };
  }, [tags.length, isBrandRefresh]);

  const handleClick = (tag) => {
    setActiveTag((prev) => {
      const newTag = prev === tag ? null : tag;
      onChange(newTag);
      return newTag;
    });
  };

  const scrollContainerStyle = {
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
    touchAction: "manipulation",
    display: "flex",
    overflowX: "auto",
    overflowY: "hidden",
    position: "relative",
    scrollSnapType: "x mandatory",
  };

  if (isBrandRefresh) {
    return (
      <div
        className={`${brStyles.row} ${className}`.trim()}
        data-part="flavor-tags"
      >
        <section
          ref={presetBoxRef}
          style={scrollContainerStyle}
          className={`no-scrollbar ${brStyles.list}`}
          onTouchStart={(e) => e.stopPropagation()}
        >
          {tags.map((tag) => {
            const isActive = activeTag === tag.title;
            return (
              <div
                key={tag.title}
                data-part="flavor-tag"
                data-selected={isActive ? "true" : "false"}
                className={`${brStyles.pill} ${
                  isActive ? brStyles.pillSelected : ""
                }`}
              >
                <div
                  className="flex flex-col gap-1 items-center justify-center"
                  onClick={() => handleClick(tag.title)}
                  onKeyPress={() => handleClick(tag.title)}
                  role="button"
                  tabIndex={0}
                >
                  <p className={brStyles.label}>
                    {getDisplayTitle(tag.title)}
                  </p>
                </div>
              </div>
            );
          })}
        </section>
      </div>
    );
  }

  return (
    <div className={`flex justify-between items-center mt-2 gap-2 ${className}`}>
      <section
        ref={presetBoxRef}
        style={scrollContainerStyle}
        className="no-scrollbar flex flex-nowrap gap-2 overflow-x-scroll max-w-max snap-x mt-2"
        onTouchStart={(e) => e.stopPropagation()}
      >
        {tags.map((tag) => {
          const isActive = activeTag === tag.title;

          return (
            <div
              key={tag.title}
              className={`flex-none snap-always snap-center
                text-dark dark:text-white
                cursor-pointer border rounded-xl p-2 border-border
                ${isActive ? "bg-dryPink text-white" : ""}
                md:hover:bg-dryPink md:hover:text-white
                transition-all duration-200`}
            >
              <div
                className="flex flex-col gap-1 items-center justify-center"
                onClick={() => handleClick(tag.title)}
                onKeyPress={() => handleClick(tag.title)}
                role="button"
                tabIndex={0}
              >
                <p className="leading-5 font-montserratMedium text-center">
                  {getDisplayTitle(tag.title)}
                </p>
              </div>
            </div>
          );
        })}
      </section>
    </div>
  );
};

Tags.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired
  })).isRequired,
  defaultActive: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  isBrandRefresh: PropTypes.bool,
};

Tags.defaultProps = {
  defaultActive: undefined,
  onChange: () => {},
  className: "",
  isBrandRefresh: false,
};

export default Tags;
